import Keycloak, { type KeycloakServerConfig } from 'keycloak-js';
import { Observable, Subject } from 'rxjs';
import { removeTokenFromLocalStorage, getTokenFromLocalStorage } from '../_helpers/localStorage';
import { installTokenResponseInterceptor } from '../_helpers/tokenResponseInterceptor';
import {
  GET_TOKEN_URL, LOGIN_URL, LOGOUT_URL, EMPTY_TOKEN_STUB, REFRESH_MAX_RETRIES, REFRESH_RETRY_DELAYS_MS,
} from '../auth.constant';
import { ITokenExtras } from '../auth.interface';
import { createProxyAdapter } from './createProxyAdapter';

export class AuthService {
  private readonly keycloak: Keycloak;

  private readonly directMode: boolean;

  private tokenExtras: ITokenExtras = { excludedScope: [] };

  /** Emits when the session is unrecoverable and the app should log out (see sessionLostEpic). */
  private readonly sessionLostSubject = new Subject<void>();

  constructor(config: KeycloakServerConfig) {
    const { clientId, url, realm } = config;
    this.directMode = Boolean(url && realm);

    this.keycloak = new Keycloak(
      this.directMode
        ? config
        : {
          clientId,
          oidcProvider: {
            authorization_endpoint: LOGIN_URL,
            token_endpoint: GET_TOKEN_URL,
            end_session_endpoint: LOGOUT_URL,
          },
        },
    );
    this.keycloak.onTokenExpired = () => this.handleTokenExpired();
  }

  init(): Promise<boolean> {
    installTokenResponseInterceptor((extras) => {
      this.tokenExtras = extras;
    });

    const {
      token = EMPTY_TOKEN_STUB,
      refreshToken = EMPTY_TOKEN_STUB,
    } = import.meta.env.DEV ? getTokenFromLocalStorage() : {};

    return this.keycloak.init({
      checkLoginIframe: false,
      responseMode: 'query',
      pkceMethod: false,
      useNonce: false,
      enableLogging: import.meta.env.DEV,
      token,
      refreshToken,
      ...(!this.directMode && {
        adapter: createProxyAdapter(() => this.keycloak.idToken),
      }),
    });
  }

  login() {
    void this.keycloak.login();// NOSONAR
  }

  logout() {
    if (import.meta.env.DEV) {
      removeTokenFromLocalStorage();
    }
    this.keycloak.logout()
      .then(() => this.keycloak.clearToken());
  }

  getInstance() {
    return this.keycloak;
  }

  isAuthenticated() {
    return this.keycloak.authenticated;
  }

  isInitialized() {
    return this.keycloak.didInitialize;
  }

  getApiToken() {
    return this.keycloak.token;
  }

  getTokenExtras(): ITokenExtras {
    return this.tokenExtras;
  }

  updateToken(forceUpdate?: boolean): Promise<boolean> {
    if (!this.directMode) {
      // Proxy mode: the real refresh token lives in a domain-wide httpOnly cookie shared by
      // Tenant Admin and every Client UI tab. Always refresh through that cookie (the .e30
      // sentinel) instead of this tab's stored token, so all tabs converge on one rotating
      // chain — refreshing with a tab's stale token trips Keycloak reuse-detection and revokes
      // the whole session (LB-2552).
      this.keycloak.refreshToken = EMPTY_TOKEN_STUB;
    }

    // check if token expired and refresh it
    return this.keycloak.updateToken(forceUpdate ? -1 : 10);
  }

  /** Fires when a token refresh fails unrecoverably; the app logs out in response. */
  sessionLost$(): Observable<void> {
    return this.sessionLostSubject.asObservable();
  }

  /**
   * keycloak-js `onTokenExpired` handler. Unlike the bare `updateToken(true)` it replaces, this
   * catches the refresh failure: keycloak-js does not re-arm its expiry timer after a failure,
   * so without this a transient error would silently stop all further refreshes. Definitive
   * failures (400/401/403) end the session; transient ones are retried with a short backoff.
   */
  private async handleTokenExpired(): Promise<void> {
    for (let attempt = 0; ; attempt++) {
      try {
        await this.updateToken(true);
        return;
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        const isDefinitive = status === 400 || status === 401 || status === 403;

        if (isDefinitive || attempt >= REFRESH_MAX_RETRIES) {
          this.sessionLostSubject.next();
          return;
        }

        const delayMs = REFRESH_RETRY_DELAYS_MS[attempt] ?? REFRESH_RETRY_DELAYS_MS[REFRESH_RETRY_DELAYS_MS.length - 1];
        await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
      }
    }
  }
}
